import { makeAutoObservable, runInAction } from 'mobx';
import { brandApi } from '@/services/brandApi';
import { inviteCodeApi } from '@/services/inviteCodeApi';
import { landingWaitlistApi } from '@/services/landingWaitlistApi';
import type { Brand, BrandItem } from '@/types';

const ACTIVE_BRAND_STORAGE_KEY = 'active-brand-id';
const BRAND_LIST_PAGE_SIZE = 100;

function normalizeBrandId(brandId: string) {
  return brandId.trim();
}

function transformBrand(apiBrand: Brand): BrandItem {
  const customAppId = apiBrand.app_id?.trim() || '';
  return {
    id: apiBrand.brand_id,
    name: apiBrand.brand_name,
    appId: customAppId || apiBrand.brand_id,
    customAppId: customAppId || undefined,
    createdAt: apiBrand.created_at,
    updatedAt: apiBrand.updated_at,
  };
}

function readPersistedBrandId() {
  if (typeof window === 'undefined') {
    return '';
  }

  const rawValue = window.localStorage.getItem(ACTIVE_BRAND_STORAGE_KEY);
  if (!rawValue) {
    return '';
  }

  return normalizeBrandId(rawValue);
}

class BrandStore {
  brands: BrandItem[] = [];
  activeBrandId = '';
  loading = false;
  creating = false;
  waitlistTotalsLoading = false;
  initialized = false;
  error = '';
  waitlistTotals: Record<string, number | null> = {};

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    this.activeBrandId = readPersistedBrandId();
  }

  get brandCount() {
    return this.brands.length;
  }

  get sortedBrands() {
    return [...this.brands].sort((left, right) =>
      left.name.localeCompare(right.name),
    );
  }

  get activeBrand() {
    return this.getBrandById(this.activeBrandId);
  }

  get activeBrandAppId() {
    return this.activeBrand?.appId || '';
  }

  requireActiveBrandAppId() {
    const appId = this.activeBrandAppId.trim();
    if (!appId) {
      throw new Error('缺少当前 App ID');
    }

    return appId;
  }

  requireActiveBrandId() {
    const brandId = this.activeBrandId.trim();
    if (!brandId) {
      throw new Error('缺少当前 Brand ID');
    }

    return brandId;
  }

  getBrandById(brandId: string) {
    const normalizedBrandId = normalizeBrandId(brandId);
    return this.brands.find((item) => item.id === normalizedBrandId) ?? null;
  }

  hasBrand(brandId: string) {
    return this.getBrandById(brandId) !== null;
  }

  setActiveBrand(brandId: string) {
    const normalizedBrandId = normalizeBrandId(brandId);
    if (!normalizedBrandId || !this.hasBrand(normalizedBrandId)) {
      return false;
    }

    this.activeBrandId = normalizedBrandId;
    this.persistActiveBrandId();
    return true;
  }

  clearActiveBrand() {
    this.activeBrandId = '';
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.removeItem(ACTIVE_BRAND_STORAGE_KEY);
  }

  async fetchBrands() {
    this.loading = true;
    this.error = '';

    try {
      const allBrands: BrandItem[] = [];
      let pageNum = 1;
      let total = 0;

      do {
        const response = await brandApi.listBrands({
          page_num: pageNum,
          page_size: BRAND_LIST_PAGE_SIZE,
        });
        const currentPageItems = response.items.map(transformBrand);
        allBrands.push(...currentPageItems);
        total = response.total || 0;

        if (currentPageItems.length === 0) {
          break;
        }
        pageNum += 1;
      } while (allBrands.length < total);

      runInAction(() => {
        const deduped = Array.from(
          new Map(allBrands.map((item) => [item.id, item])).values(),
        );
        this.brands = deduped;
        this.loading = false;
        this.initialized = true;

        if (this.activeBrandId && !this.hasBrand(this.activeBrandId)) {
          this.clearActiveBrand();
        }
      });
    } catch (error) {
      runInAction(() => {
        this.loading = false;
        this.initialized = true;
        this.error = error instanceof Error ? error.message : '加载 Brand 失败';
      });
      throw error;
    }
  }

  async createBrand(brandName: string, appId?: string) {
    const trimmedName = brandName.trim();
    const trimmedAppId = appId?.trim() || '';

    if (!trimmedName) {
      throw new Error('Brand 名称不能为空');
    }
    if (trimmedName.length > 30) {
      throw new Error('Brand 名称不能超过 30 个字');
    }
    if (/\s/.test(trimmedAppId)) {
      throw new Error('App ID 不能包含空白字符');
    }
    if (trimmedAppId.length > 128) {
      throw new Error('App ID 不能超过 128 个字符');
    }

    this.creating = true;
    try {
      const response = await brandApi.createBrand({
        brand_name: trimmedName,
        app_id: trimmedAppId || undefined,
      });
      const newBrand = transformBrand(response.brand);

      runInAction(() => {
        const exists = this.brands.some((item) => item.id === newBrand.id);
        this.brands = exists
          ? this.brands.map((item) =>
              item.id === newBrand.id ? newBrand : item,
            )
          : [newBrand, ...this.brands];
        this.waitlistTotals[newBrand.id] = 0;
        this.creating = false;
      });

      return newBrand;
    } catch (error) {
      runInAction(() => {
        this.creating = false;
      });
      throw error;
    }
  }

  getWaitlistTotal(brandId: string) {
    const normalizedBrandId = normalizeBrandId(brandId);
    return this.waitlistTotals[normalizedBrandId];
  }

  async fetchWaitlistTotals() {
    if (this.brands.length === 0) {
      runInAction(() => {
        this.waitlistTotals = {};
        this.waitlistTotalsLoading = false;
      });
      return;
    }

    this.waitlistTotalsLoading = true;

    try {
      const entries = await Promise.all(
        this.brands.map(async (brand) => {
          try {
            if (isOjoBrand(brand.name)) {
              const response = await inviteCodeApi.listInviteApplications({
                app_id: brand.appId,
                page_num: 1,
                page_size: 1,
              });
              return [brand.id, response.total || 0] as const;
            }

            const response = await landingWaitlistApi.listAdmin(brand.appId, {
              page_num: 1,
              page_size: 1,
            });
            return [brand.id, response.total || 0] as const;
          } catch (_error) {
            return [brand.id, null] as const;
          }
        }),
      );

      runInAction(() => {
        this.waitlistTotals = Object.fromEntries(entries);
        this.waitlistTotalsLoading = false;
      });
    } catch (_error) {
      runInAction(() => {
        this.waitlistTotalsLoading = false;
      });
    }
  }

  private persistActiveBrandId() {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(ACTIVE_BRAND_STORAGE_KEY, this.activeBrandId);
  }
}

export const brandStore = new BrandStore();

function isOjoBrand(brandName: string) {
  return brandName.trim().toLowerCase() === 'ojo';
}
